/**
 * Error monitoring in the browser (M36). Only when VITE_SENTRY_DSN is set at
 * build time; otherwise this is a no-op and the app runs exactly as before.
 *
 * What never leaves the phone: the personal key. It appears in exactly one
 * place — the /me/<key> link — and beforeSend blanks it out of any URL Sentry
 * would otherwise send. Nothing else identifying is attached: no email, no
 * name; a signed-in staff member is tagged by user id only.
 */
import * as Sentry from '@sentry/react';

const KEY_IN_PATH = /\/me\/[A-Za-z0-9_-]{43}/g;

function scrub(s) {
  return typeof s === 'string' ? s.replace(KEY_IN_PATH, '/me/[key]') : s;
}

export function initMonitoring() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return false;
  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    sendDefaultPii: false,
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    beforeSend(event) {
      if (event.request?.url) event.request.url = scrub(event.request.url);
      if (event.transaction) event.transaction = scrub(event.transaction);
      for (const b of event.breadcrumbs ?? []) {
        if (b.data?.url) b.data.url = scrub(b.data.url);
        if (b.data?.to) b.data.to = scrub(b.data.to);
        if (b.data?.from) b.data.from = scrub(b.data.from);
        if (b.message) b.message = scrub(b.message);
      }
      return event;
    },
  });
  return true;
}

/** Tag events with the signed-in staff user's id (never their email). */
export function setMonitoringUser(id) {
  Sentry.setUser(id ? { id } : null);
}

export const MonitoringBoundary = Sentry.ErrorBoundary;
export default { initMonitoring, setMonitoringUser };
